<template>
    <PageWrapper>
        <template #headline>
            <fa icon="fa-sack-dollar" />&nbsp;{{ asset.asset_title }}
        </template>

        <template #actions>
            <BackButton
                v-if="$router.options.history.state.back"
                @click="router.go(-1)"
            />
            <BackButton
                v-if="$router.options.history.state.back !== '/assets'"
                icon="fa-list"
                title="assets"
                @click="toAssets"
            />
            <EditButton
                v-if="userStore.can('update_asset')"
                title="edit"
                @click="editAsset"
            />
            <DeleteButton
                v-if="userStore.can('delete_asset')"
                @click="openDeleteModal"
            />
            <ActionsMenu
                v-if="
                    userStore.can('view_assets') ||
                    userStore.can('transfer_money') ||
                    userStore.can('trigger_ad_hoc_iban_reminder') ||
                    userStore.can('retrieve_nyala_registry_extract') ||
                    userStore.can('create_nyala_registry_extract') ||
                    userStore.can('send_nyala_registry_document_email') ||
                    userStore.can('transfer_nyala_tokens') ||
                    userStore.can('update_stakeholder_amount') ||
                    userStore.can('send_opt_ins') ||
                    userStore.can('release_funds') ||
                    userStore.can('upload_secondary_market_import') ||
                    userStore.can(
                        'export_secondary_market_asset_pre_emptive_shares'
                    )
                "
            />
        </template>

        <template #modals>
            <DeleteItemModal
                id="delete-asset-modal"
                v-model="asset"
                v-model:open="deleteModalOpen"
                title="modal_title_delete_asset"
                name-key="asset_title"
                id-key="asset_uid"
                @submit="deleteAsset"
            />
            <ConnectNyalaModal
                id="connect-nyala-modal"
                ref="connectNyalaModal"
                v-model:nyalaProject="nyalaProjectEdit"
                v-model:open="connectNyalaModalOpen"
                title="manage_nyala_project"
                @submit="connectNyalaProjectToAsset"
            />
            <DetachNyalaModal
                id="detach-nyala-modal"
                ref="detachNyalaModal"
                v-model:nyalaProject="nyalaProject"
                v-model:open="detachNyalaModalOpen"
                title="detach_nyala_project"
                @submit="handleDetachNyalaProject"
            />
        </template>

        <SummaryWrapper>
            <AssetSummary v-if="assetDetailsItems" />

            <template
                v-if="
                    userStore.is('developer') ||
                    asset.contract ||
                    asset.voucher_contract ||
                    asset.stock_emission_contract ||
                    issuer
                "
                #sidebar
            >
                <ContractSummary v-if="asset.contract" />
                <VoucherContractSummary v-if="asset.voucher_contract" />
                <StockEmissionContractSummery
                    v-if="asset.stock_emission_contract"
                />
                <IssuerSummary v-if="issuer" />
                <PlatformSummary v-if="userStore.is('developer')" />
                <NyalaProjectSummary
                    v-if="
                        nyalaProject !== null ||
                        assetContractsAllConnectedToNyalaProject === true
                    "
                />
            </template>
        </SummaryWrapper>

        <CampaignsTable
            v-if="asset.asset_uid"
            :initial-filters="{
                asset_uid: asset.asset_uid,
            }"
            :create-initial-values="campaignsTableCreateInitialValues"
            :create-disabled-fields="['asset_uid']"
        />
        <OrdersTable
            v-if="asset.asset_uid"
            :initial-filters="{
                asset_uid: asset.asset_uid,
            }"
        />
        <ContractsTable
            v-if="asset.asset_title"
            :initial-filters="{
                asset_uid: asset.asset_uid,
                asset_title: asset.asset_title,
            }"
        />
        <AssetLinkedPlatformDocumentsTable v-if="asset.asset_uid">
            <template #actions>
                <EditButton
                    v-if="userStore.can('update_asset')"
                    aria-controls="asset-linked-platform-documents-modals"
                    @click.stop="handleEditLinkedPlatformDocuments"
                />
            </template>
        </AssetLinkedPlatformDocumentsTable>
        <ContractDocumentsTable>
            <template #actions>
                <DeleteBulkButton
                    v-if="userStore.can('update_asset')"
                    v-model:selected="selectedContractDocuments"
                    @click.stop="handleDeleteAssetContractDocuments"
                />
                <CreateButton
                    v-if="userStore.can('update_asset')"
                    aria-controls="asset-contract-documents-modals"
                    @click.stop="handleUpdateOrCreateAssetContractDocument"
                />
            </template>
        </ContractDocumentsTable>
        <ReportingDocumentsTable>
            <template #actions>
                <DeleteBulkButton
                    v-if="userStore.can('update_asset')"
                    v-model:selected="selectedReportingDocuments"
                    @click.stop="handleDeleteAssetReportingDocuments"
                />
                <CreateButton
                    v-if="userStore.can('update_asset')"
                    aria-controls="asset-reporting-documents-modals"
                    @click.stop="handleUpdateOrCreateAssetReportingDocument"
                />
            </template>
        </ReportingDocumentsTable>
        <ConsumerInformationDocumentsTable>
            <template #actions>
                <DeleteBulkButton
                    v-if="userStore.can('update_asset')"
                    v-model:selected="selectedConsumerInformationDocuments"
                    @click.stop="handleDeleteAssetConsumerInformationDocuments"
                />
                <CreateButton
                    v-if="userStore.can('update_asset')"
                    aria-controls="asset-consumer-information-documents-modals"
                    @click.stop="
                        handleUpdateOrCreateAssetConsumerInformationDocument
                    "
                />
            </template>
        </ConsumerInformationDocumentsTable>
        <AssetSignatureDocumentsTable
            v-if="asset.asset_uid"
            :asset="asset"
        />
        <AuditsTable
            v-if="asset.asset_uid"
            :initial-filters="{
                auditable_id: asset.asset_uid,
                auditable_type: auditedModels.asset.value,
            }"
        />
        <OrderBooksTable
            v-if="platform.platform_name"
            :initial-filters="{
                platform_name_exact: platform.platform_name,
                asset_uid: asset.asset_uid,
            }"
        />
        <PreEmptiveRightsTable
            v-if="asset.sm_marketplace"
            :initial-filters="{
                asset_uid: asset.asset_uid,
            }"
        />
        <TransferToProjectWalletModal
            v-model:open="transferToProjectWalletModalOpen"
            :payment-account-type="(asset.payment_account?.type as PaymentAccountType)"
            :asset="asset"
            @submit="transferToProjectWallet"
        />
        <RunExclusiveAcceptanceModal
            v-model:open="runExclusiveAcceptanceModalOpen"
            :platform="platform"
            :payment-account-type="(asset.payment_account?.type as PaymentAccountType)"
            @submit="runExclusiveAcceptance"
        />
        <ModalWrapper v-if="isModalOpen" />
    </PageWrapper>
</template>

<script setup lang="ts">
import { useAssetDocumentsTable } from '@composables/assets/useAssetDocumentsTable'
import { useAssetLinkedPlatformDocuments } from '@composables/assets/useAssetLinkedPlatformDocuments'
import {
    ModalButtonStyles,
    useActionsMenu,
    useModal,
} from '@composables/common'
import AssetSignatureDocumentsTable from '@partials/assets/AssetSignatureDocumentsTable.vue'
import UploadAssetInvestmentReport from '@partials/assets/UploadAssetInvestmentReport.vue'
import UploadAssetSecondaryMarketImport from '@partials/assets/UploadAssetSecondaryMarketImport.vue'
import CampaignsTable from '@partials/campaigns/CampaignsTable.vue'
import OrdersTable from '@partials/orders/OrdersTable.vue'
import PreEmptiveRightsTable from '@partials/secondaryMarket/PreEmptiveRightsTable.vue'
import { DeleteItemModal, NumberField, SummaryWrapper } from '@src/components'
import { useEnum, useSummary } from '@src/composables/common'
import { useContractSummary } from '@src/composables/summaries/useContractSummary'
import { useIssuerSummary } from '@src/composables/summaries/useIssuerSummary'
import { useNyalaProjectSummary } from '@src/composables/summaries/useNyalaProjectSummary'
import { usePaymentAccountSummary } from '@src/composables/summaries/usePaymentAccountSummary'
import { usePlatformSummary } from '@src/composables/summaries/usePlatformSummary'
import {
    AssetCategory,
    AssetDocumentType,
    AssetStatus,
    AssetSubStatus,
    StockType,
} from '@src/enums'
import { PaymentAccountType } from '@src/enums/PaymentAccount'
import BackButton from '@src/partials/actions/BackButton.vue'
import CreateButton from '@src/partials/actions/CreateButton.vue'
import DeleteBulkButton from '@src/partials/actions/DeleteBulkButton.vue'
import DeleteButton from '@src/partials/actions/DeleteButton.vue'
import EditButton from '@src/partials/actions/EditButton.vue'
import RoundPicker from '@src/partials/assets/RoundPicker.vue'
import RunExclusiveAcceptanceModal from '@src/partials/assets/RunExclusiveAcceptanceModal.vue'
import TransferToProjectWalletModal from '@src/partials/assets/TransferToProjectWalletModal.vue'
import AuditsTable from '@src/partials/audits/AuditsTable.vue'
import ContractsTable from '@src/partials/contracts/ContractsTable.vue'
import ConnectNyalaModal from '@src/partials/nyalaPlatforms/ConnectNyalaProjectModal.vue'
import DetachNyalaModal from '@src/partials/nyalaPlatforms/DetachNyalaProjectModal.vue'
import PageWrapper from '@src/partials/pages/PageWrapper.vue'
import {
    Asset,
    AssetDocument,
    Campaign,
    Contract,
    Issuer,
    NyalaProject,
    Order,
    PaymentMethods,
    Platform,
    PlatformDocument,
} from '@src/types'
import { SummaryItem } from '@src/types/Summary'
import {
    AcceptanceRound,
    deleteAsset as apiDeleteAsset,
    exportAssetInvestmentReport as apiExportAssetInvestmentReport,
    exportAssetSignatureDocuments as apiExportAssetSignatureDocuments,
    exportCheckOrdersReport as apiExportCheckOrdersReport,
    previewAssetSecondaryMarketImport as apiPreviewAssetSecondaryMarketImport,
    releaseFunds as apiReleaseFunds,
    runExclusiveAcceptance as apiRunExclusiveAcceptance,
    sendAdHocEmailReminder as apiSendAdHocEmailReminder,
    sendOptIns as apiSendOptIns,
    transferTokens as apiTransferTokens,
    transferToProjectWallet as apiTransferToProjectWallet,
    updateStakeholderAmount as apiUpdateStakeholderAmount,
    uploadAssetInvestmentReport as apiUploadAssetInvestmentReport,
    uploadAssetSecondaryMarketImport as apiUploadAssetSecondaryMarketImport,
    closeAcceptanceRound,
    createNyalaRegistryExtract,
    getAsset,
    retrieveNyalaRegistryExtract,
    runBulkSetPaidOrders,
    runBulkSetPendingOrders,
    SecondaryMarketImportReviewItem,
    sendNyalaRegistryDocumentMail,
    updateAsset,
} from '@src/utils/api/assets'
import {
    anyAssetContractConnectedToNyalaProject as apiAnyAssetContractConnectedToNyalaProject,
    areAssetContractsAllConnectedToNyalaProject as apiAreAssetContractsAllConnectedToNyalaProject,
    createNyalaProject as apiCreateNyalaProject,
    detachNyalaProject as apiDetachNyalaProject,
    getNyalaProjectForAsset as apiGetNyalaProject,
    updateNyalaProject as apiUpdateNyalaProject,
    getNyalaProjectForContract,
} from '@src/utils/api/nyalaProjects'
import { getOrders } from '@src/utils/api/orders'
import { getPlatform } from '@src/utils/api/platforms'
import { UploadFileResponse } from '@src/utils/api/s3'
import { exportSecondaryMarketAssetPreEmptiveRights as apiExportSecondaryMarketAssetPreEmptiveRights } from '@src/utils/api/secondaryMarket'
import { zohoPushSingleAsset } from '@src/utils/api/zoho'
import { formatNumericValues } from '@src/utils/helpers'
import { useAuditsStore } from '@src/utils/stores/audits'
import { useCountryStore } from '@src/utils/stores/countries'
import { useToastStore } from '@src/utils/stores/toast'
import { useUserStore } from '@src/utils/stores/user'
import { computedAsync } from '@vueuse/core'
import Decimal from 'decimal.js'
import moment from 'moment'
import { storeToRefs } from 'pinia'
import { computed, h, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'

const route = useRoute()
const router = useRouter()
const toast = useToastStore()
const i18n = useI18n()
const { ModalWrapper, openModal } = useModal()
const isModalOpen = ref(false)
const userStore = useUserStore()
const countryStore = useCountryStore()
const { getEnumLabel } = useEnum()
const auditsStore = useAuditsStore()

const { auditedModels } = storeToRefs(auditsStore)

const asset = ref<Asset>(new Asset())
const contract = ref<Contract>(new Contract())
const issuer = ref<Issuer>(new Issuer())
const voucherContract = ref<Contract>(new Contract())
const stockEmissionContract = ref<Contract>(new Contract())
const contracts = ref<Contract[]>([])
const contractDocuments = ref<AssetDocument[]>([])
const reportingDocuments = ref<AssetDocument[]>([])
const consumerInformationDocuments = ref<AssetDocument[]>([])
const platformDocuments = ref<PlatformDocument[]>([])
const platform = ref<Platform>(new Platform())
const orders = ref<Order[] | null>(null)
const ordersCount = ref<number>(0)
const deleteModalOpen = ref<boolean>(false)
const transferToProjectWalletModalOpen = ref<boolean>(false)
const nyalaProject = ref<NyalaProject | null>(null)
const connectNyalaModalOpen = ref<boolean>(false)
const detachNyalaModalOpen = ref<boolean>(false)
const runExclusiveAcceptanceModalOpen = ref<boolean>(false)

// State variables for Secondary Market Import Review
const previewLoading = ref(false)
const previewData = ref<SecondaryMarketImportReviewItem[]>([])
const previewSummary = ref({ total: 0, invalid: 0 })

const { ActionsMenu } = useActionsMenu(
    {
        id: 'asset-actions-menu',
        align: 'right',
    },
    [
        {
            label: 'run_bulk_set_pending_orders',
            icon: 'fa-circle-play',
            color: 'text-warning-600',
            action: handleRunBulkSetPendingOrders,
            condition: () => userStore.can('transfer_money'),
            needsWarning: true,
        },
        {
            label: 'run_bulk_set_paid_orders',
            icon: 'fa-circle-play',
            color: 'text-warning-600',
            action: handleRunBulkSetPaidOrders,
            condition: () => userStore.can('transfer_money'),
            needsWarning: true,
        },
        {
            label: 'export_asset_investment_report',
            icon: 'fa-sack-dollar',
            color: 'text-primary-accent-600',
            action: exportAssetInvestmentReport,
            condition: () => userStore.can('view_assets'),
        },
        {
            label: 'export_signature_documents',
            icon: ' fa-file',
            color: 'text-primary-accent-600',
            action: exportAssetSignatureDocuments,
            condition: () => userStore.can('view_assets'),
        },
        {
            label: 'upload_asset_investment_report',
            icon: 'fa-file-import',
            color: 'text-info-500',
            action: handleUploadAssetInvestmentReport,
            condition: () => userStore.can('transfer_money'),
        },
        {
            label: 'run_exclusive_acceptance',
            icon: 'fa-circle-play',
            color: 'text-warning-600',
            action: handleRunExclusiveAcceptance,
            condition: () => userStore.can('transfer_money'),
            needsWarning: true,
        },
        {
            label: 'run_inclusive_acceptance',
            icon: 'fa-money-bill-transfer',
            color: 'text-primary-accent-600',
            action: openTransferToProjectWallet,
            condition: () => userStore.can('transfer_money'),
            needsWarning: true,
        },
        {
            label: 'update_stakeholder_amount',
            icon: 'fa-percent',
            color: 'text-primary-accent-600',
            action: handleUpdateStakeholderAmount,
            condition: () =>
                asset.value.payment_account?.type === 'secupay' &&
                userStore.can('update_stakeholder_amount'),
        },
        {
            label: 'release_funds',
            icon: 'fa-lock-open',
            color: 'text-primary-accent-600',
            action: handleReleaseFunds,
            condition: () =>
                asset.value.payment_account?.type === 'secupay' &&
                userStore.can('release_funds'),
        },
        {
            label: 'send_opt_ins',
            icon: 'fa-user-check',
            color: 'text-blue-600',
            action: handleSendOptIns,
            condition: () =>
                asset.value.has_nyala_project && userStore.can('send_opt_ins'),
        },
        {
            label: 'transfer_tokens',
            icon: 'fa-hand-holding-dollar',
            color: 'text-blue-500',
            action: handleTransferTokens,
            condition: () =>
                userStore.can('transfer_nyala_tokens') &&
                (nyalaProject.value !== null ||
                    nyalaProjectForContract.value !== null),
        },
        {
            label: 'create_nyala_registry_extract',
            icon: 'fa-gear',
            color: 'text-blue-600',
            action: handleCreateNyalaRegistryExtract,
            condition: () =>
                asset.value.has_nyala_project &&
                userStore.can('create_nyala_registry_extract'),
            needsWarning: true,
        },
        {
            label: 'retrieve_nyala_registry_extract',
            icon: 'fa-download',
            color: 'text-blue-600',
            action: handleRetrieveNyalaRegistryExtract,
            condition: () =>
                asset.value.has_nyala_project &&
                userStore.can('retrieve_nyala_registry_extract'),
        },
        {
            label: 'send_nyala_registry_document_email',
            icon: 'fa-paper-plane',
            color: 'text-blue-600',
            action: handleSendNyalaRegistryDocumentMail,
            condition: () =>
                asset.value.has_nyala_project &&
                userStore.can('send_nyala_registry_document_email'),
        },
        {
            label: 'send_ad_hoc_email_reminder',
            icon: 'fa-paper-plane',
            color: 'text-blue-600',
            action: sendAdHocEmailReminder,
            condition: () => userStore.can('trigger_ad_hoc_iban_reminder'),
        },
        {
            label: 'export_check_orders_report',
            icon: 'fa-list-check',
            color: 'text-info-500',
            action: exportCheckOrdersReport,
            condition: () => userStore.can('view_assets'),
        },
        {
            label: 'manage_nyala_project',
            icon: 'fa-arrow-right-arrow-left',
            color: 'text-gray-500',
            action: handleOpenConnectNyalaModal,
            condition: () =>
                userStore.can('connect_nyala_project_to_asset_or_contract') &&
                anyAssetContractConnectedToNyalaProject.value === false,
        },
        {
            label: 'detach_nyala_project',
            icon: 'fa-trash-can',
            color: 'text-gray-500',
            action: handleOpenDetachNyalaProjectModal,
            condition: () =>
                userStore.can('detach_nyala_project_from_asset_or_contract') &&
                nyalaProject.value !== null &&
                ordersCount.value === 0,
            needsWarning: true,
        },
        {
            label: 'push_to_zoho',
            icon: 'fa-file-import',
            color: 'text-blue-500',
            action: pushToZoho,
            condition: () =>
                asset.value.zoho_sync_enabled && userStore.can('update_asset'),
        },
        {
            label: 'upload_asset_secondary_market_import',
            icon: 'fa-file-import',
            color: 'text-info-500',
            action: handleUploadAssetSecondaryMarketImport,
            condition: () => userStore.can('upload_secondary_market_import'),
        },
        {
            label: 'export_asset_secondary_market_pre_emptive_rights',
            icon: 'fa-file-export',
            color: 'text-info-500',
            action: exportSecondaryMarketAssetPreEmptiveRights,
            condition: () =>
                userStore.can(
                    'export_secondary_market_asset_pre_emptive_rights'
                ),
        },
    ]
)

const campaignsTableCreateInitialValues = computed(() => {
    return {
        ...new Campaign(),
        ...{
            asset_uid: asset.value.asset_uid,
            platform_name: asset.value.platform_name,
        },
    }
})

const clearingSystem = computed(() => {
    switch (asset.value.clearing_system) {
        case 'CI':
            return i18n.t('clearing_system_ci')
        case 'CF':
            return i18n.t('clearing_system_cf')
        case 'EMISSION':
            return i18n.t('clearing_system_emission')
        case 'STOCK_EMISSION':
            return i18n.t('clearing_system_stock_emission')
        default:
            return asset.value.clearing_system
    }
})

const { ContractSummary } = useContractSummary(contract)
const { IssuerSummary } = useIssuerSummary(issuer)

const { ContractSummary: VoucherContractSummary } = useContractSummary(
    voucherContract,
    {
        title: 'voucher_contract',
    }
)

const { ContractSummary: StockEmissionContractSummery } = useContractSummary(
    stockEmissionContract,
    {
        title: 'stock_emission_contract',
    }
)

const { PlatformSummary } = usePlatformSummary(platform)

const assetDetailsItems = computedAsync<SummaryItem[]>(async () => {
    let fundingLimits: SummaryItem[] = [
        { label: i18n.t('interest_rate'), value: asset.value.interest_rate },
        { label: i18n.t('target_yield'), value: asset.value.target_yield },
        {
            label: i18n.t('asset_investment_min'),
            value: formatNumericValues(asset.value.investment_min as number),
        },
        {
            label: i18n.t('asset_investment_max'),
            value: formatNumericValues(asset.value.investment_max as number),
        },
        {
            label: i18n.t('asset_step_level'),
            value: asset.value.step_level,
        },
        {
            label: i18n.t('asset_funding_min'),
            value: formatNumericValues(asset.value.funding_min as number),
        },
        {
            label: i18n.t('asset_funding_max'),
            value: formatNumericValues(asset.value.funding_max as number),
        },
    ]

    if (asset.value.category === AssetCategory.STOCK_EMISSION) {
        fundingLimits = [
            {
                label: i18n.t('asset_price_per_stock'),
                value: formatNumericValues(
                    asset.value.price_per_stock as number
                ),
            },
            {
                label: i18n.t('asset_minimum_investment'),
                value: formatNumericValues(
                    asset.value.minimum_investment as number
                ),
            },
            {
                label: i18n.t('asset_stock_increments'),
                value: formatNumericValues(
                    asset.value.stock_increments as number
                ),
            },
            {
                label: i18n.t('asset_number_of_stocks'),
                value: formatNumericValues(
                    asset.value.number_of_stocks as number
                ),
            },
            {
                label: i18n.t('asset_maximum_total_investment_value'),
                value: formatNumericValues(
                    asset.value.maximum_total_investment_value as number
                ),
            },
            {
                label: i18n.t('asset_stock_type'),
                value: getEnumLabel(StockType, asset.value.stock_type, true),
            },
            { label: i18n.t('asset_isin'), value: asset.value.isin },
        ]
    }

    return [
        {
            label: i18n.t('id'),
            value: asset.value.asset_uid,
            copy: true,
        },
        {
            label: i18n.t('asset_name'),
            value: asset.value.asset_title,
            copy: true,
        },
        {
            label: i18n.t('Status'),
            value: getEnumLabel(AssetStatus, asset.value.status, true),
        },
        {
            label: i18n.t('Sub Status'),
            value: getEnumLabel(AssetSubStatus, asset.value.sub_status, true),
        },
        {
            label: i18n.t('city'),
            value: asset.value.asset_location_city,
            copy: true,
        },
        {
            label: i18n.t('asset_location_state'),
            value: asset.value.asset_location_state,
            copy: true,
        },
        {
            label: i18n.t('asset_location_country'),
            value: await countryStore.translate(
                asset.value.asset_location_country as string
            ),
            copy: true,
        },
        { label: i18n.t('currency'), value: asset.value.currency },
        {
            label: i18n.t('asset_category'),
            value: getEnumLabel(AssetCategory, 1, true),
        },
        {
            label: i18n.t('clearing_system'),
            value: clearingSystem.value,
        },
        { label: i18n.t('contract_range'), value: asset.value.contract_range },
        ...fundingLimits,
        {
            label: i18n.t('asset_base_funding_count'),
            value: formatNumericValues(
                asset.value.base_funding_count as number
            ),
            condition: !!asset.value.base_funding_count,
        },
        {
            label: i18n.t('asset_base_funding_total'),
            value: formatNumericValues(
                asset.value.base_funding_total as number
            ),
            condition: !!asset.value.base_funding_total,
        },
        {
            label: i18n.t('asset_funding_count'),
            value: formatNumericValues(asset.value.funding_count as number),
        },
        {
            label: i18n.t('asset_funding_total'),
            value: formatNumericValues(asset.value.funding_total as number),
        },
        {
            label: i18n.t('asset_minimum_initial_payment'),
            value: formatNumericValues(
                asset.value.minimum_initial_payment as number
            ),
            condition: asset.value.minimum_initial_payment !== null,
        },
        {
            label: i18n.t('asset_maximum_initial_payment'),
            value: formatNumericValues(
                asset.value.maximum_initial_payment as number
            ),
            condition: asset.value.maximum_initial_payment !== null,
        },
        {
            label: i18n.t('asset_initial_payment_step'),
            value: asset.value.initial_payment_step,
            condition: asset.value.initial_payment_step !== null,
        },
        {
            label: i18n.t('asset_manual_funding_totals'),
            value: asset.value.manual_funding_totals,
            special: 'check',
        },
        {
            label: i18n.t('asset_deactivate_payment_account'),
            value: asset.value.deactivate_payment_account,
            special: 'check',
        },
        {
            label: i18n.t('asset_installment_investment'),
            value: asset.value.installment_investment,
            special: 'check',
        },
        {
            label: i18n.t('asset_installment_payout'),
            value: asset.value.installment_payout,
            special: 'check',
            condition: !!asset.value.installment_investment,
        },
        {
            label: i18n.t('asset_minimum_installment'),
            value: formatNumericValues(
                asset.value.minimum_installment as number
            ),
            condition: !!asset.value.installment_investment,
        },
        {
            label: i18n.t('asset_maximum_installment'),
            value: formatNumericValues(
                asset.value.maximum_installment as number
            ),
            condition: !!asset.value.installment_investment,
        },
        {
            label: i18n.t('asset_installment_step'),
            value: asset.value.installment_step,
            condition: !!asset.value.installment_investment,
        },
        {
            label: i18n.t('asset_minimum_monthly_rates'),
            value: asset.value.minimum_monthly_rates,
            condition: !!asset.value.installment_investment,
        },
        {
            label: i18n.t('asset_maximum_monthly_rates'),
            value: asset.value.maximum_monthly_rates,
            condition: !!asset.value.installment_investment,
        },
        {
            label: i18n.t('asset_minimum_yearly_rates'),
            value: asset.value.minimum_yearly_rates,
            condition: !!asset.value.installment_investment,
        },
        {
            label: i18n.t('asset_maximum_yearly_rates'),
            value: asset.value.maximum_yearly_rates,
            condition: !!asset.value.installment_investment,
        },
        {
            label: i18n.t('require_qualified_signature'),
            value: asset.value.require_qualified_signature,
            special: 'check',
        },
        {
            label: i18n.t('signature_type'),
            value: asset.value.signature_sproof
                ? i18n.t('signature_type_sproof')
                : asset.value.signature_manual
                ? i18n.t('signature_type_manual')
                : i18n.t('none'),
        },
        {
            label: i18n.t('signature_manual_confirmation_required'),
            value: asset.value.signature_manual_confirmation_required,
            special: 'check',
            condition: !!asset.value.signature_manual,
        },
        {
            label: i18n.t('contract_period'),
            value: asset.value.contract_period,
            condition: asset.value.contract_period !== null,
        },
        {
            label: i18n.t('tags'),
            value: asset.value.tags,
            copy: true,
            condition: asset.value.tags !== null,
        },
        {
            label: i18n.t('sm_marketplace'),
            value: asset.value.sm_marketplace,
            special: 'check',
        },
        {
            label: i18n.t('created_at'),
            value: moment(asset.value.created_on).format('DD.MM.yyyy HH:mm'),
        },
        {
            label: i18n.t('updated_at'),
            value: moment(asset.value.updated_at).format('DD.MM.yyyy HH:mm'),
        },
    ]
})

const { Summary: AssetSummary } = useSummary(assetDetailsItems, {
    title: 'summary',
})

const linkedPlatformDocumentIds = computed<string[]>({
    get: () => asset.value.linked_platform_documents,
    set: (value) => {
        asset.value.linked_platform_documents = value
    },
})

const { AssetLinkedPlatformDocumentsTable, handleEditLinkedPlatformDocuments } =
    useAssetLinkedPlatformDocuments(
        linkedPlatformDocumentIds,
        platformDocuments,
        update,
        openModal,
        isModalOpen
    )

const {
    AssetDocumentsTableNew: ContractDocumentsTable,
    selectedAssetDocumentsIds: selectedContractDocuments,
    handleUpdateOrCreateAssetDocument:
        handleUpdateOrCreateAssetContractDocument,
    handleDeleteAssetDocuments: handleDeleteAssetContractDocuments,
} = useAssetDocumentsTable(
    contractDocuments,
    AssetDocumentType['CONTRACT_DATA'],
    update,
    openModal,
    isModalOpen
)

const {
    AssetDocumentsTableNew: ReportingDocumentsTable,
    selectedAssetDocumentsIds: selectedReportingDocuments,
    handleUpdateOrCreateAssetDocument:
        handleUpdateOrCreateAssetReportingDocument,
    handleDeleteAssetDocuments: handleDeleteAssetReportingDocuments,
} = useAssetDocumentsTable(
    reportingDocuments,
    AssetDocumentType['REPORTING'],
    update,
    openModal,
    isModalOpen
)

const {
    AssetDocumentsTableNew: ConsumerInformationDocumentsTable,
    selectedAssetDocumentsIds: selectedConsumerInformationDocuments,
    handleUpdateOrCreateAssetDocument:
        handleUpdateOrCreateAssetConsumerInformationDocument,
    handleDeleteAssetDocuments: handleDeleteAssetConsumerInformationDocuments,
} = useAssetDocumentsTable(
    consumerInformationDocuments,
    AssetDocumentType['CONSUMER_INFORMATION'],
    update,
    openModal,
    isModalOpen
)

const nyalaProjectForContract = ref<boolean>(false)
const assetContractsAllConnectedToNyalaProject = ref<boolean>(false)
const anyAssetContractConnectedToNyalaProject = ref<boolean>(false)
const contractAssetConnectedToNyalaProject = ref<null>(null)
const { NyalaProjectSummary } = useNyalaProjectSummary(
    nyalaProject,
    assetContractsAllConnectedToNyalaProject,
    contractAssetConnectedToNyalaProject
)

const { PaymentAccountSummary } = usePaymentAccountSummary(
    computed(() => asset.value?.payment_account ?? null)
)

function toAssets(): void {
    router.push({ name: 'assets' })
}

function editAsset(): void {
    router.push({ name: 'asset.edit', params: { id: asset.value.asset_uid } })
}

function openDeleteModal(): void {
    deleteModalOpen.value = true
}

async function deleteAsset() {
    if (!asset.value?.asset_uid) return
    await apiDeleteAsset(asset.value?.asset_uid)
    toast.success(
        i18n.t('toast_success_asset_deleted', { asset: asset.value?.asset_uid })
    )
    router.push({ name: 'assets' })
}

async function load() {
    if (route.params.id) {
        const assetRequest = await getAsset(route.params.id as string)
        asset.value = assetRequest.data.asset
        contract.value = asset.value.contract as Contract
        issuer.value = asset.value.issuer as Issuer
        voucherContract.value = asset.value.voucher_contract as Contract
        stockEmissionContract.value = asset.value
            .stock_emission_contract as Contract
        contracts.value = asset.value.contracts
        const nyalaProjectRequest = await apiGetNyalaProject(
            route.params.id as string
        )
        nyalaProject.value = nyalaProjectRequest.data.nyalaProject

        if (asset.value.contract && asset.value.contract.contract_uid) {
            const nyalaProjectForContractRequest =
                await getNyalaProjectForContract(
                    asset.value.contract.contract_uid
                )
            nyalaProjectForContract.value =
                nyalaProjectForContractRequest.data.nyalaProject !== null
        }
        const anyAssetContractConnectedToNyalaProjectRequest =
            await apiAnyAssetContractConnectedToNyalaProject(
                route.params.id as string
            )
        anyAssetContractConnectedToNyalaProject.value =
            anyAssetContractConnectedToNyalaProjectRequest.data.any_asset_contract_connected_to_nyala_project
        // Check if all asset contracts are connected to Nyala project
        if (!nyalaProjectRequest.data.nyalaProject) {
            const areAssetContractsAllConnectedToNyalaProjectRequest =
                await apiAreAssetContractsAllConnectedToNyalaProject(
                    route.params.id as string
                )
            assetContractsAllConnectedToNyalaProject.value =
                areAssetContractsAllConnectedToNyalaProjectRequest.data.asset_contracts_all_connected_to_nyala_project
        }

        contractDocuments.value = asset.value.asset_documents.filter(
            (assetDocument) =>
                assetDocument.type === AssetDocumentType['CONTRACT_DATA']
        )
        reportingDocuments.value = asset.value.asset_documents.filter(
            (assetDocument) =>
                assetDocument.type === AssetDocumentType['REPORTING']
        )
        consumerInformationDocuments.value = asset.value.asset_documents.filter(
            (assetDocument) =>
                assetDocument.type === AssetDocumentType['CONSUMER_INFORMATION']
        )
        if (asset.value.payment_methods === null) {
            asset.value.payment_methods = new PaymentMethods()
        }

        const platformRequest = await getPlatform(
            asset.value.platform_name as string
        )
        platform.value = platformRequest.data.platform
        platformDocuments.value = platform.value.documents

        const filters = ref<{ [key: string]: string | null }>({})
        filters.value['asset_uid'] = asset.value.asset_uid

        const orderRequest = await getOrders({
            filter: filters.value,
        })

        orders.value = orderRequest.data.orders
        ordersCount.value = orderRequest.data.orders_count
    }
}

async function update(successMessage: string | null = null) {
    asset.value.asset_documents = [
        ...contractDocuments.value,
        ...reportingDocuments.value,
        ...consumerInformationDocuments.value,
    ]
    await updateAsset(asset.value)
    if (successMessage) {
        toast.success(successMessage)
    }
    await load()
}

async function exportAssetInvestmentReport() {
    await apiExportAssetInvestmentReport(asset.value)
        .catch((error) => {
            throw error
        })
        .then(() => {
            toast.success(i18n.t('toast_success_export_csv'))
        })
}

async function exportAssetSignatureDocuments() {
    await apiExportAssetSignatureDocuments(asset.value)
        .catch((error) => {
            throw error
        })
        .then(() => {
            toast.success(i18n.t('toast_success_export_csv'))
        })
}

async function uploadAssetInvestmentReport(
    assetInvestmentReportFile: UploadFileResponse
) {
    isModalOpen.value = false

    await apiUploadAssetInvestmentReport(
        asset.value,
        assetInvestmentReportFile.key
    )
        .catch((error) => {
            throw error
        })
        .then(() => {
            toast.success(i18n.t('toast_success_upload_csv'))
        })
}

async function uploadAssetSecondaryMarketImport(
    assetInvestmentReportFile: UploadFileResponse
) {
    isModalOpen.value = false

    await apiUploadAssetSecondaryMarketImport(
        asset.value,
        assetInvestmentReportFile.key
    )
        .catch((error) => {
            throw error
        })
        .then(() => {
            toast.success(i18n.t('toast_success_upload_csv'))
            previewData.value = []
        })
}

function handleUploadAssetInvestmentReport() {
    isModalOpen.value = true
    const assetInvestmentReportFile = ref<UploadFileResponse | null>(null)

    const disableCreate = computed(() => {
        return !assetInvestmentReportFile.value
    })

    openModal(
        {
            id: 'upload_asset_investment_report',
            title: 'upload_asset_investment_report',
            open: isModalOpen,
            onCancel: () => (isModalOpen.value = false),
            onSubmit: () =>
                assetInvestmentReportFile.value &&
                uploadAssetInvestmentReport(assetInvestmentReportFile.value),
        },
        () =>
            h(UploadAssetInvestmentReport, {
                modelValue: assetInvestmentReportFile,
            }),
        {
            submitButtonText: 'upload',
            styles: {
                submitButton: ModalButtonStyles['BLUE'],
            },
            submitButtonDisabled: disableCreate,
        }
    )
}

function handleUploadAssetSecondaryMarketImport() {
    isModalOpen.value = true
    const secondaryMarketImportFile = ref<UploadFileResponse | null>(null)
    const isPreviewLoaded = ref(false)

    watch(secondaryMarketImportFile, async (file) => {
        if (!file || !file.key) {
            previewData.value = []
            previewSummary.value = { total: 0, invalid: 0 }
            isPreviewLoaded.value = false
            previewLoading.value = false
            return
        }

        previewLoading.value = true
        try {
            const response = await apiPreviewAssetSecondaryMarketImport(
                asset.value,
                file.key
            )
            previewData.value = response.data.data || []
            previewSummary.value = {
                total: response.data.total_count || 0,
                invalid: response.data.invalid_count || 0,
            }
            isPreviewLoaded.value = true
        } catch (error) {
            toast.error(i18n.t('toast_error_preview_failed'))
            previewData.value = []
            isPreviewLoaded.value = false
        } finally {
            previewLoading.value = false
        }
    })

    const disableCreate = computed(() => {
        return (
            !secondaryMarketImportFile.value ||
            !secondaryMarketImportFile.value?.key ||
            previewLoading.value ||
            !isPreviewLoaded.value
        )
    })

    openModal(
        {
            id: 'upload_asset_secondary_market_import',
            title: 'upload_asset_secondary_market_import',
            open: isModalOpen,
            onCancel: () => {
                isModalOpen.value = false
                previewData.value = []
                secondaryMarketImportFile.value = null
                isPreviewLoaded.value = false
            },
            onSubmit: () => {
                if (
                    !secondaryMarketImportFile.value ||
                    !secondaryMarketImportFile.value.key ||
                    !isPreviewLoaded.value ||
                    previewLoading.value
                ) {
                    return
                }

                uploadAssetSecondaryMarketImport(
                    secondaryMarketImportFile.value
                )
            },
        },
        () =>
            h('div', { class: 'space-y-4' }, [
                h(UploadAssetSecondaryMarketImport, {
                    'modelValue': secondaryMarketImportFile.value,
                    'onUpdate:modelValue': (val: UploadFileResponse | null) => {
                        secondaryMarketImportFile.value = val
                        if (!val) {
                            previewData.value = []
                            isPreviewLoaded.value = false
                        }
                    },
                }),

                previewLoading.value
                    ? h(
                          'div',
                          { class: 'p-4 text-center text-sm text-gray-500' },
                          i18n.t('loading_preview')
                      )
                    : null,

                previewData.value.length > 0
                    ? h(
                          'div',
                          {
                              class: 'mt-4 border rounded-lg overflow-hidden text-xs',
                          },
                          [
                              h(
                                  'div',
                                  {
                                      class: 'p-2 bg-gray-100 font-bold flex justify-between',
                                  },
                                  [
                                      h(
                                          'span',
                                          `${i18n.t('total')}: ${
                                              previewSummary.value.total
                                          }`
                                      ),
                                      previewSummary.value.invalid > 0
                                          ? h(
                                                'span',
                                                {
                                                    class: 'text-red-600 font-bold',
                                                },
                                                `${i18n.t('invalid')}: ${
                                                    previewSummary.value.invalid
                                                }`
                                            )
                                          : h(
                                                'span',
                                                {
                                                    class: 'text-green-600 font-bold',
                                                },
                                                i18n.t('all_rows_valid')
                                            ),
                                  ]
                              ),
                              h('div', { class: 'max-h-60 overflow-y-auto' }, [
                                  h(
                                      'table',
                                      {
                                          class: 'w-full text-left border-collapse',
                                      },
                                      [
                                          h(
                                              'thead',
                                              {
                                                  class: 'bg-gray-50 border-b',
                                              },
                                              [
                                                  h('tr', [
                                                      h(
                                                          'th',
                                                          { class: 'p-2' },
                                                          '#'
                                                      ),
                                                      h(
                                                          'th',
                                                          { class: 'p-2' },
                                                          'Existing?'
                                                      ),
                                                      h(
                                                          'th',
                                                          { class: 'p-2' },
                                                          'Account Type'
                                                      ),
                                                      h(
                                                          'th',
                                                          { class: 'p-2' },
                                                          'Name (Email)'
                                                      ),
                                                      h(
                                                          'th',
                                                          {
                                                              class: 'p-2 text-right',
                                                          },
                                                          i18n.t('amount')
                                                      ),
                                                      h(
                                                          'th',
                                                          {
                                                              class: 'p-2 text-center',
                                                          },
                                                          i18n.t('status')
                                                      ),
                                                  ]),
                                              ]
                                          ),
                                          h(
                                              'tbody',
                                              previewData.value.map(
                                                  (
                                                      row: SecondaryMarketImportReviewItem
                                                  ) =>
                                                      h(
                                                          'tr',
                                                          {
                                                              class: row.is_valid
                                                                  ? 'border-b hover:bg-gray-50'
                                                                  : 'border-b bg-red-50',
                                                          },
                                                          [
                                                              h(
                                                                  'td',
                                                                  {
                                                                      class: 'p-2 text-gray-400',
                                                                  },
                                                                  row.row_index
                                                              ),

                                                              h(
                                                                  'td',
                                                                  {
                                                                      class: 'p-2 font-mono text-[10px]',
                                                                  },
                                                                  row.existing_or_new
                                                              ),
                                                              h(
                                                                  'td',
                                                                  {
                                                                      class: 'p-2',
                                                                  },
                                                                  row.profile_type
                                                              ),
                                                              h(
                                                                  'td',
                                                                  {
                                                                      class: 'p-2',
                                                                  },
                                                                  row.profile_name
                                                                      ? `${row.profile_name} (${row.profile_email})`
                                                                      : 'N/A'
                                                              ),
                                                              h(
                                                                  'td',
                                                                  {
                                                                      class: 'p-2 text-right font-bold',
                                                                  },
                                                                  row.pre_emptive_rights
                                                              ),
                                                              h(
                                                                  'td',
                                                                  {
                                                                      class: 'p-2 text-center',
                                                                  },
                                                                  row.is_valid
                                                                      ? h(
                                                                            'span',
                                                                            {
                                                                                class: 'text-green-600 font-bold',
                                                                            },
                                                                            '✓ OK'
                                                                        )
                                                                      : h(
                                                                            'span',
                                                                            {
                                                                                class: 'text-red-600 font-bold',
                                                                                title:
                                                                                    row.error_reason ||
                                                                                    '',
                                                                            },
                                                                            '✗'
                                                                        )
                                                              ),
                                                          ]
                                                      )
                                              )
                                          ),
                                      ]
                                  ),
                              ]),
                          ]
                      )
                    : null,
            ]),
        {
            submitButtonText: 'upload',
            styles: {
                submitButton: ModalButtonStyles['BLUE'],
            },
            submitButtonDisabled: disableCreate,
        }
    )
}

async function exportCheckOrdersReport() {
    await apiExportCheckOrdersReport(asset.value)
        .catch((error) => {
            throw error
        })
        .then((res) => {
            if (res.data.tasks) {
                toast.success(i18n.t('toast_success_export_csv'))
            } else {
                toast.success(res.data.message)
            }
        })
}

async function exportSecondaryMarketAssetPreEmptiveRights() {
    await apiExportSecondaryMarketAssetPreEmptiveRights(asset.value)
        .catch((error) => {
            throw error
        })
        .then((res) => {
            if (res.data.tasks) {
                toast.success(
                    res.data?.message
                        ? res.data?.message
                        : i18n.t('toast_success_export_csv')
                )
            } else {
                toast.success(res.data.message)
            }
        })
}

async function sendAdHocEmailReminder() {
    await apiSendAdHocEmailReminder(asset.value)
    toast.success(i18n.t('toast_success_ad_hoc_email_sent'))
}

// Closing a round is purely admin bookkeeping (see backend docblock), so a
// failure here must never block or roll back the actual action's result —
// it just gets its own error toast.
async function closeRounds(roundIds: number[]) {
    if (roundIds.length === 0) {
        return
    }
    const results = await Promise.allSettled(
        roundIds.map((roundId) => closeAcceptanceRound(asset.value, roundId))
    )
    const failedRoundIds = roundIds.filter(
        (_, index) => results[index].status === 'rejected'
    )
    if (failedRoundIds.length > 0) {
        toast.error(
            i18n.t('toast_error_closing_acceptance_rounds', {
                rounds: failedRoundIds.join(', '),
            })
        )
    }
}

// Opens a modal that only asks the admin to pick an acceptance round (via
// RoundPicker) before running a round-scoped action. Shared by every action
// that doesn't need extra input beyond "which round".
function openRoundScopedActionModal(options: {
    id: string
    title: string
    submitButtonText: string
    onConfirm: (roundId: number) => Promise<void>
}) {
    isModalOpen.value = true
    const selectedRoundId = ref<number | null>(null)
    const roundsToClose = ref<number[]>([])

    const disableSubmit = computed(() => selectedRoundId.value === null)

    openModal(
        {
            id: options.id,
            title: options.title,
            open: isModalOpen,
            onCancel: () => (isModalOpen.value = false),
            onSubmit: () => {
                if (selectedRoundId.value === null) {
                    return
                }
                isModalOpen.value = false
                options.onConfirm(selectedRoundId.value)
                closeRounds(roundsToClose.value)
            },
        },
        () =>
            h(RoundPicker, {
                'asset': asset.value,
                'modelValue': selectedRoundId.value,
                'onUpdate:modelValue': (value: number | null) => {
                    selectedRoundId.value = value
                },
                'onUpdate:roundsToClose': (ids: number[]) => {
                    roundsToClose.value = ids
                },
            }),
        {
            submitButtonText: options.submitButtonText,
            styles: {
                submitButton: ModalButtonStyles['BLUE'],
            },
            submitButtonDisabled: disableSubmit,
        }
    )
}

// Only triggers generation with Nyala — no round to pick, so this skips the
// round-picker modal entirely and just confirms (via needsWarning) before
// firing the request. Nyala needs some time after this before the documents
// are actually ready; Download Registry Extracts (separate action) fetches
// them once they are.
async function handleCreateNyalaRegistryExtract() {
    const response = await createNyalaRegistryExtract(asset.value)
    toast.success(
        i18n.t('toast_success_create_nyala_registry_extract', {
            log: response.data.log,
        })
    )
}

function handleRetrieveNyalaRegistryExtract() {
    openRoundScopedActionModal({
        id: 'retrieve_nyala_registry_extract',
        title: 'modal_title_retrieve_nyala_registry_extract',
        submitButtonText: 'download',
        onConfirm: async (roundId) => {
            const response = await retrieveNyalaRegistryExtract(
                asset.value,
                roundId
            )
            toast.success(
                i18n.t('toast_success_retrieve_nyala_registry_extract', {
                    log: response.data.log,
                })
            )
        },
    })
}

function handleSendNyalaRegistryDocumentMail() {
    openRoundScopedActionModal({
        id: 'send_nyala_registry_document_email',
        title: 'modal_title_send_nyala_registry_document_email',
        submitButtonText: 'send',
        onConfirm: async (roundId) => {
            const response = await sendNyalaRegistryDocumentMail(
                asset.value,
                roundId
            )
            if (response.data.success) {
                toast.success(
                    i18n.t('toast_success_send_nyala_registry_document_email')
                )
            }
        },
    })
}

function handleUpdateStakeholderAmount() {
    isModalOpen.value = true
    const selectedRoundId = ref<number | null>(null)
    const selectedRound = ref<AcceptanceRound | null>(null)
    const roundsToClose = ref<number[]>([])
    const feeInputMode = ref<'amount' | 'percentage'>('amount')
    // Amount mode: admin enters the currency amount to distribute; the
    // equivalent percentage is derived from the round's total accepted amount.
    const stakeholderFee = ref<number | null>(null)
    // Percentage mode: admin enters the rate directly.
    const directFeePercentage = ref<number | null>(null)
    const derivedFeePercentage = ref<number | null>(null)

    const configuredStakeholderFee = computed(
        () =>
            asset.value.contract?.stakeholder_fee ??
            asset.value.payment_account?.stakeholder_fee ??
            null
    )

    watch(stakeholderFee, (fee) => {
        const totalAmount = selectedRound.value?.total_amount ?? null
        if (fee != null && fee > 0 && totalAmount != null && totalAmount > 0) {
            derivedFeePercentage.value = new Decimal(fee)
                .div(totalAmount)
                .mul(100)
                .toNumber()
        } else {
            derivedFeePercentage.value = null
        }
    })

    // A different round has a different total accepted amount, so a
    // previously entered amount/percentage no longer means the same thing.
    watch(selectedRoundId, () => {
        stakeholderFee.value = null
        directFeePercentage.value = null
        derivedFeePercentage.value = null
    })

    const disableSubmit = computed(() => {
        if (selectedRoundId.value === null) {
            return true
        }
        return feeInputMode.value === 'amount'
            ? stakeholderFee.value === null
            : directFeePercentage.value === null
    })

    openModal(
        {
            id: 'update_stakeholder_amount',
            title: 'modal_title_update_stakeholder_amount',
            open: isModalOpen,
            onCancel: () => (isModalOpen.value = false),
            onSubmit: async () => {
                if (selectedRoundId.value === null) {
                    return
                }
                const feePercentage =
                    feeInputMode.value === 'amount'
                        ? stakeholderFee.value === 0
                            ? 0
                            : derivedFeePercentage.value
                        : directFeePercentage.value
                if (feePercentage === null) {
                    return
                }
                isModalOpen.value = false
                const response = await apiUpdateStakeholderAmount(
                    asset.value,
                    selectedRoundId.value,
                    new Decimal(feePercentage).div(100).toNumber()
                )
                toast.success(response.data.message)
                closeRounds(roundsToClose.value)
            },
        },
        () =>
            h('div', { class: 'space-y-4' }, [
                h(RoundPicker, {
                    'asset': asset.value,
                    'modelValue': selectedRoundId.value,
                    'onUpdate:modelValue': (value: number | null) => {
                        selectedRoundId.value = value
                    },
                    'onUpdate:selectedRound': (
                        round: AcceptanceRound | null
                    ) => {
                        selectedRound.value = round
                    },
                    'onUpdate:roundsToClose': (ids: number[]) => {
                        roundsToClose.value = ids
                    },
                }),
                h('div', { class: 'flex justify-between text-sm' }, [
                    h(
                        'span',
                        { class: 'font-medium text-gray-700' },
                        i18n.t('label_configured_stakeholder_fee')
                    ),
                    h(
                        'span',
                        { class: 'text-gray-900' },
                        configuredStakeholderFee.value != null
                            ? (configuredStakeholderFee.value * 100).toFixed(
                                  2
                              ) + '%'
                            : '—'
                    ),
                ]),
                h('div', { class: 'flex gap-6 text-sm' }, [
                    h(
                        'label',
                        { class: 'flex cursor-pointer items-center gap-2' },
                        [
                            h('input', {
                                type: 'radio',
                                checked: feeInputMode.value === 'amount',
                                onChange: () => (feeInputMode.value = 'amount'),
                            }),
                            h(
                                'span',
                                { class: 'text-gray-700' },
                                i18n.t('label_fee_input_mode_amount')
                            ),
                        ]
                    ),
                    h(
                        'label',
                        { class: 'flex cursor-pointer items-center gap-2' },
                        [
                            h('input', {
                                type: 'radio',
                                checked: feeInputMode.value === 'percentage',
                                onChange: () =>
                                    (feeInputMode.value = 'percentage'),
                            }),
                            h(
                                'span',
                                { class: 'text-gray-700' },
                                i18n.t('label_fee_input_mode_percentage')
                            ),
                        ]
                    ),
                ]),
                feeInputMode.value === 'amount'
                    ? h('div', [
                          h(NumberField, {
                              'modelValue': stakeholderFee.value,
                              'onUpdate:modelValue': (value: number | null) => {
                                  stakeholderFee.value = value
                              },
                              'name': 'stakeholder_fee',
                              'label': 'label_invoice_fee_to_distribute',
                              'help': 'help_invoice_fee_to_distribute',
                              'min': 0,
                              'allowNull': true,
                          }),
                          stakeholderFee.value === 0
                              ? h(
                                    'div',
                                    { class: 'text-sm text-gray-600' },
                                    i18n.t(
                                        'label_stakeholder_fee_will_be_removed'
                                    )
                                )
                              : derivedFeePercentage.value != null
                              ? h(
                                    'div',
                                    { class: 'text-sm text-gray-600' },
                                    `${i18n.t(
                                        'label_derived_fee_percentage'
                                    )}: ${derivedFeePercentage.value}%`
                                )
                              : null,
                      ])
                    : h('div', [
                          h(NumberField, {
                              'modelValue': directFeePercentage.value,
                              'onUpdate:modelValue': (value: number | null) => {
                                  directFeePercentage.value = value
                              },
                              'name': 'fee_percentage',
                              'label': 'label_fee_percentage_direct',
                              'help': 'help_fee_percentage_direct',
                              'min': 0,
                              'max': 100,
                              'maximumFractionDigits': 15,
                              'allowNull': true,
                          }),
                          directFeePercentage.value === 0
                              ? h(
                                    'div',
                                    { class: 'text-sm text-gray-600' },
                                    i18n.t(
                                        'label_stakeholder_fee_will_be_removed'
                                    )
                                )
                              : null,
                      ]),
            ]),
        {
            submitButtonText: 'update',
            styles: {
                submitButton: ModalButtonStyles['BLUE'],
            },
            submitButtonDisabled: disableSubmit,
        }
    )
}

function handleSendOptIns() {
    openRoundScopedActionModal({
        id: 'send_opt_ins',
        title: 'modal_title_send_opt_ins',
        submitButtonText: 'send_opt_ins',
        onConfirm: async (roundId) => {
            const response = await apiSendOptIns(asset.value, roundId)
            toast.success(response.data.message)
        },
    })
}

function handleReleaseFunds() {
    openRoundScopedActionModal({
        id: 'release_funds',
        title: 'modal_title_release_funds',
        submitButtonText: 'release_funds',
        onConfirm: async (roundId) => {
            const response = await apiReleaseFunds(asset.value, roundId)
            toast.success(response.data.message)
        },
    })
}

function handleTransferTokens() {
    openRoundScopedActionModal({
        id: 'transfer_tokens',
        title: 'modal_title_transfer_tokens',
        submitButtonText: 'transfer_tokens',
        onConfirm: async (roundId) => {
            const response = await apiTransferTokens(asset.value, roundId)
            toast.success(response.data.message)
        },
    })
}

function openTransferToProjectWallet() {
    transferToProjectWalletModalOpen.value = true
}

function handleRunExclusiveAcceptance() {
    runExclusiveAcceptanceModalOpen.value = true
}

async function handleRunBulkSetPendingOrders() {
    const response = await runBulkSetPendingOrders(asset.value)
    if (response.status === 200) {
        toast.success(response.data.message as string)
    }
}

async function handleRunBulkSetPaidOrders() {
    const response = await runBulkSetPaidOrders(asset.value)
    if (response.status === 200) {
        toast.success(response.data.message as string)
    }
}
async function transferToProjectWallet(data: { wallet: string; date: string }) {
    const response = await apiTransferToProjectWallet(
        asset.value,
        data.wallet,
        data.date
    )
    toast.success(
        i18n.t('toast_success_run_inclusive_acceptance', {
            log: response.data.log,
        })
    )
    transferToProjectWalletModalOpen.value = false
}

async function runExclusiveAcceptance(data: { wallet: string }) {
    const response = await apiRunExclusiveAcceptance(asset.value, data.wallet)
    toast.success(
        i18n.t('toast_success_run_exclusive_acceptance', {
            log: response.data.log,
        })
    )
    runExclusiveAcceptanceModalOpen.value = false
}

const nyalaProjectEdit = ref<NyalaProject | null>(null)

function handleOpenConnectNyalaModal() {
    if (nyalaProject.value) {
        nyalaProjectEdit.value = nyalaProject.value
    } else {
        nyalaProjectEdit.value = new NyalaProject()
        nyalaProjectEdit.value.asset_id = asset.value.asset_uid
        nyalaProjectEdit.value.contract_id = null
    }
    connectNyalaModalOpen.value = true
}

async function connectNyalaProjectToAsset() {
    if (nyalaProject.value) {
        // Update pre-existing NyalaProject connection
        await apiUpdateNyalaProject(nyalaProject.value)
        toast.success(i18n.t('toast_success_nyala_project_connected_to_asset'))
        connectNyalaModalOpen.value = false
    }
    await load()
    if (!nyalaProject.value && nyalaProjectEdit.value) {
        // Create new Nyala project connection
        await apiCreateNyalaProject(nyalaProjectEdit.value)
        toast.success(i18n.t('toast_success_nyala_project_connected_to_asset'))
        connectNyalaModalOpen.value = false
    }
    await load()
}

async function handleOpenDetachNyalaProjectModal() {
    if (nyalaProject.value && nyalaProject.value.id) {
        detachNyalaModalOpen.value = true
    }
}

async function handleDetachNyalaProject() {
    if (nyalaProject.value && nyalaProject.value.id) {
        await apiDetachNyalaProject(nyalaProject.value.id)
        detachNyalaModalOpen.value = false
        toast.success(
            i18n.t('toast_success_nyala_project_detached_from_contract')
        )
        await load()
    }
}

async function pushToZoho() {
    await zohoPushSingleAsset(asset.value.asset_uid as string)
    toast.success(i18n.t('toast_success_asset_push_to_zoho'))
}

watch(
    () => route.params.id,
    async () => {
        await load()
    }
)

onMounted(async () => {
    load()
})
</script>

<style scoped lang="sass">
.details
    @apply flex flex-col gap-4 grow
    .detail
        .label
            @apply font-bold mr-2
            &:after
                content: ':'
        .value
            a
                @apply text-blue-500
</style>
